<template>
  <div class="widget__content-center widget__content-center--flex">
    <div v-if="loading">
      <div class="loading-spinner" />
    </div>
    <div class="widget-benchmark" v-else>
      <div
        style="
          width: 25px;
          height: 25px;
          display: flex;
          justify-content: center;
          align-items: center;
        "
      >
        <slot name="icon" />
      </div>
      <div class="widget-benchmark__value" :class="trendingClass">
        {{ formatValue(eventValue) }}
      </div>
      <div class="widget__label">
        <slot name="label" />
      </div>
      <div class="widget-benchmark__category-selector-container" v-if="isBenchmarking">
        <InputSelect
          v-if="widget.data.benchmarks?.length && widget.data.benchmarks?.length > 1"
          v-model="benchmarkData"
          :options="widget.data.benchmarks"
          optionLabel="category_name"
          @optionSelected="(opt) => setBenchmark(opt?.category_id ?? 0)"
          class="widget-benchmark__select"
        />
        <div v-else class="widget-benchmark__label">
          {{ widget.data.benchmarks?.[0]?.category_name }}
        </div>
      </div>
      <div class="widget-benchmark__container" v-if="isBenchmarking">
        <div v-if="widget.data.benchmarks?.[0]?.benchmarks?.is_low_sample_size">
          <Tooltip>
            <template #tooltip>
              We're still collecting benchmark data for this type of zone. Check back soon for
              comparisons.
            </template>
            <template #text>
              <div class="widget-benchmark__low-sample-size-container">
                <IconInfo class="widget-benchmark__low-sample-size-icon" />
                <div class="widget-benchmark__low-sample-size-vertical-divider" />
                <div class="widget-benchmark__low-sample-size-label">
                  <p>Benchmarking:</p>
                  <p>Sample size too low</p>
                </div>
              </div>
            </template>
          </Tooltip>
        </div>
        <div v-else>
          <div class="widget-benchmark__trend" v-if="benchmark">
            <template v-if="aboveBenchmark">
              <IconTrendingUp />
              <p>{{ formatNumber((eventValue / benchmark) * 100, 2) }}% above benchmark</p>
            </template>
            <template v-else>
              <IconTrendingDown />
              <p>{{ formatNumber((1 - eventValue / benchmark) * 100, 2) }}% below benchmark</p>
            </template>
          </div>
          <a href="/faq?category=benchmarking" target="_blank" class="widget-benchmark__learn-more">
            <span>Learn more</span>
            <IconExternalLink />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import InputSelect from '@/components/inputs/InputSelect.vue';
import Tooltip from '@/components/global/Tooltip.vue';

import IconExternalLink from '@/icons/IconExternalLink.svg?component';
import IconInfo from '@/icons/IconInfo.svg?component';
import IconTrendingUp from '@/icons/analytics/IconTrendingUp.svg?component';
import IconTrendingDown from '@/icons/analytics/IconTrendingDown.svg?component';

import { getBenchmarkingDataByWidgetAndZone } from '@/utils/api';
import { formatNumber } from '@/utils/formatNumbers';
import { secToMinHrSec } from '@/utils/formatTimeSpent';
import type { WidgetFilter, WidgetSetting, WidgetTypeVisits } from '@/types/Widget';

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { useAnalyticsStore } from '@/stores/analytics';
const analyticsStore = useAnalyticsStore();

const props = defineProps<{
  area?: WidgetFilter;
  zone?: WidgetSetting;
  widget: WidgetTypeVisits;
  valueType: 'decimal' | 'integer' | 'percentage' | 'time';
}>();

const widget = ref(props.widget);
const loading = ref(false);

const benchmarkData = ref(
  widget.value.data.benchmarks?.length ? widget.value.data.benchmarks?.[0] : undefined,
);

watch(
  () => props.widget,
  (newWidget) => {
    widget.value = newWidget;
    setBenchmark();
  },
);

const eventValue = computed(() => {
  return benchmarkData.value?.event_value ?? 0;
});

const benchmark = computed(() => {
  return benchmarkData.value?.benchmarks?.mean ?? undefined;
});

const isBenchmarking = computed(() => {
  if (!analyticsStore.isBenchmarking) return false;
  return benchmark.value !== undefined;
});

const aboveBenchmark = computed(() => {
  return isBenchmarking.value && (benchmark.value ?? 0) < eventValue.value;
});

const trendingClass = computed(() => {
  if (!isBenchmarking.value) return 'not-benchmarking';
  return aboveBenchmark.value ? 'trending-up' : 'trending-down';
});

const formatValue = (value: number) => {
  switch (props.valueType) {
    case 'time':
      return secToMinHrSec(value).text;
    case 'percentage':
      return `${formatNumber(value, 2)}%`;
    case 'integer':
      return formatNumber(value, 0);
    default:
      return formatNumber(value, 2);
  }
};

watch(
  () => [props.zone, props.area],
  (newVal) => {
    if (!newVal) return;

    loading.value = true;

    getBenchmarkingDataByWidgetAndZone(
      userStore.accessToken,
      analyticsStore.chosenEvent?.id ?? 0,
      widget.value.id ?? 0,
      props.area?.name === 'Site' ? undefined : props.zone?.id,
    )
      .then((resp) => {
        widget.value = resp;
      })
      .then(() => {
        setBenchmark();
      })
      .finally(() => {
        loading.value = false;
      })
      .catch(() => {
        loading.value = false;
      });
  },
);

const setBenchmark = (categoryId?: number) => {
  if (!categoryId) {
    benchmarkData.value = widget.value.data?.benchmarks?.[0];
  } else {
    benchmarkData.value = widget.value.data?.benchmarks?.find(
      (bench) => bench.category_id === categoryId,
    );
  }
};
</script>

<style lang="scss">
@use 'sass:color';

.widget-benchmark {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.widget-benchmark__value {
  @include sizing.margin('lg', 'auto');
  @include sizing.padding();
  @include typography.font-display($size: md, $weight: bold);
  display: inline-block;
}

.not-benchmarking {
  @include mixins.rounded-border($color: variables.$primitives-color-primary-500);
  background: color.adjust(variables.$primitives-color-primary-100, $lightness: 20%);
  color: variables.$primitives-color-primary-500;
}

.trending-up {
  @include mixins.rounded-border($color: variables.$primitives-color-blue-300);
  background: variables.$primitives-color-background-300;
  color: variables.$primitives-color-blue-300;
}

.trending-down {
  @include mixins.rounded-border($color: variables.$primitives-color-secondary-700);
  background: variables.$primitives-color-secondary-100;
  color: variables.$primitives-color-secondary-700;
}

p {
  color: variables.$primitives-color-tertiary-400;
}

.widget-benchmark__container {
  margin-top: variables.$sizing-sm;
  gap: variables.$sizing-xxs;
}

.widget-benchmark__data {
  @include mixins.flex-center();
  @include sizing.padding('xxxs', 'xxs');
}

.widget-benchmark__label {
  @include sizing.margin('xs');
  @include sizing.padding('xxxs', 'xxs');
  @include typography.font-display($size: xxs, $weight: bold);
  display: inline-block;
  @include mixins.rounded-border($color: variables.$primitives-color-tertiary-300);
  background: color.adjust(variables.$primitives-color-tertiary-300, $lightness: 20%);
  color: variables.$primitives-color-tertiary-500;
}

.widget-benchmark__trend {
  @include mixins.flex-center($gap: variables.$sizing-xxs);
}

.widget-benchmark__category-selector-container {
  @include mixins.flex-center($gap: variables.$sizing-xxs);
  margin: variables.$sizing-xxs;
  flex-wrap: wrap;
}

.widget-benchmark__category-selector {
  @include sizing.padding('xxxs', 'xxs');
  @include typography.font-display($size: xxs, $weight: bold);
  display: inline-block;
  @include mixins.rounded-border($color: variables.$primitives-color-tertiary-300);
  background: color.adjust(variables.$primitives-color-tertiary-300, $lightness: 20%);
  color: variables.$primitives-color-tertiary-500;
}

.widget-benchmark__category-selector--active {
  @include mixins.rounded-border($color: variables.$primitives-color-primary-500);
  background: color.adjust(variables.$primitives-color-primary-100, $lightness: 20%);
  color: variables.$primitives-color-primary-500;
}

.widget-benchmark__low-sample-size-container {
  @include sizing.padding('xxs', 'xs');
  background-color: color.adjust(variables.$primitives-color-secondary-100, $lightness: 10%);
  border-radius: variables.$sizing-sm;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  text-align: left;

  .widget-benchmark__low-sample-size-vertical-divider {
    height: variables.$sizing-sm;
    width: 1px;
    background-color: variables.$primitives-color-tertiary-300;
    margin: 0 variables.$sizing-sm;
  }

  .widget-benchmark__low-sample-size-icon {
    color: variables.$primitives-color-tertiary-400;
    width: variables.$sizing-sm;
  }

  .widget-benchmark__low-sample-size-label {
    @include mixins.flex-center($gap: variables.$sizing-xxxs);
    @include typography.font-display($size: xxs, $weight: regular);
    color: variables.$primitives-color-tertiary-400;
    display: block;
    text-align: left;
    white-space: nowrap;

    p {
      margin: 0;
      text-align: left;
    }
  }
}

.widget-benchmark__learn-more {
  @include typography.font-body($size: xs, $weight: medium);
  color: variables.$primitives-color-tertiary-400;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: variables.$sizing-xxxs;
  text-align: center;
}
</style>
